import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { LoginInput, SignUpInput } from '@auditwire/contracts';
import { supabase } from '@/shared/lib/supabase/client';
import { authRepository } from './api/authRepository';
import { mapProfileToUser } from './model/mapProfile';
import type { AuthStatus, AuthUser } from './model/types';
import { AuthContext, type AuthContextValue } from './authContext';

/**
 * Owns the auth session lifecycle: hydrates from the persisted session, keeps the
 * profile in sync via `onAuthStateChange`, and exposes sign-in/up/out actions.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const sessionRef = useRef<Session | null>(null);

  const applySession = useCallback(async (session: Session | null) => {
    sessionRef.current = session;
    if (!session?.user) {
      setUser(null);
      setStatus('unauthenticated');
      return;
    }
    const profile = await authRepository.getProfile(session.user.id);
    if (profile) {
      setUser(mapProfileToUser(profile, session.user.email ?? ''));
      setStatus('authenticated');
    } else {
      // Profile not provisioned yet (trigger race) — treat as signed out for now.
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) void applySession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) void applySession(session);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = useCallback((input: LoginInput) => authRepository.signIn(input), []);
  const signUp = useCallback((input: SignUpInput) => authRepository.signUp(input), []);
  const signOut = useCallback(() => authRepository.signOut(), []);
  const refreshProfile = useCallback(() => applySession(sessionRef.current), [applySession]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, signIn, signUp, signOut, refreshProfile }),
    [user, status, signIn, signUp, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
